class BundleMixMatch extends HTMLElement {
  connectedCallback() {
    this.bundleSize       = parseInt(this.dataset.bundleSize, 10) || 8;
    this.parentVariantId  = this.dataset.parentVariantId;
    this.allowDuplicates  = this.dataset.allowDuplicates !== 'false';

    this.counts         = {};   // variantId → integer count
    this.variantData    = {};   // variantId → { gid, title, image }
    this._adding        = false;
    this.rows        = Array.from(this.querySelectorAll('.js-bmm-row'));
    this.counter     = this.querySelector('.js-bmm-counter');
    this.trayItems   = this.querySelector('.js-bmm-tray-items');
    this.trayEmpty   = this.querySelector('.js-bmm-tray-empty');
    this.trayCounter = this.querySelector('.js-bmm-tray-counter');
    this.rail = this.querySelector('.js-bmm-rail');
    this.cta       = this.querySelector('.js-bmm-cta');
    this.hint      = this.querySelector('.js-bmm-hint');
    this.errorEl   = this.querySelector('.js-bmm-error');

    if (!this.cta || !this.errorEl) return;

    this.rows.forEach((row) => {
      const { variantId, variantGid, variantTitle, variantImage, variantNotes } = row.dataset;
      this.counts[variantId]      = 0;
      this.variantData[variantId] = { gid: variantGid, title: variantTitle, image: variantImage, notes: variantNotes || '' };

      const increment  = row.querySelector('.js-bmm-increment');
      const decrement  = row.querySelector('.js-bmm-decrement');

      // Stepper — stop propagation so clicks don't also toggle expand
      increment.addEventListener('click', (e) => { e.stopPropagation(); this._onIncrement(row); });
      decrement.addEventListener('click', (e) => { e.stopPropagation(); this._onDecrement(row); });

      row.querySelector('.js-bmm-details-btn')?.addEventListener('click', () => this._openDetails(row));
    });

    this.cta.addEventListener('click', () => this._onAddToCart());

    this._initRail();
    this._initDescriptionExpand();
    this._initDetails();
    this._updateUI();
    this._prefillFromFinder();
  }

  _prefillFromFinder() {
    if (this.dataset.hofPrefill !== 'true') return;
    const value = new URLSearchParams(window.location.search).get('hof_samples');
    if (!value || value.length > 3000) return;

    const rowsByHandle = new Map(this.rows.map((row) => [row.dataset.productHandle, row]));
    const chosen = new Set();
    for (const handle of value.split(',').slice(0, 50)) {
      if (!/^[a-z0-9-]+$/.test(handle) || chosen.has(handle)) continue;
      const row = rowsByHandle.get(handle);
      if (!row) continue;
      chosen.add(handle);
      this._onIncrement(row);
      if (this._totalSelected() >= this.bundleSize) break;
    }

    const selected = this._totalSelected();
    const note = this.querySelector('.js-bmm-finder-note');
    if (!note || !selected) return;
    const template = selected === this.bundleSize ? note.dataset.complete : note.dataset.partial;
    note.textContent = template.replace('[count]', String(selected));
    note.hidden = false;
  }

  _initDetails() {
    this.detailsDialog = this.querySelector('.js-bmm-details-dialog');
    this.querySelector('.js-bmm-details-close')?.addEventListener('click', () => this.detailsDialog.close());
    this.detailsIncrement = this.querySelector('.js-bmm-details-increment');
    this.detailsDecrement = this.querySelector('.js-bmm-details-decrement');
    this.detailsCount = this.querySelector('.js-bmm-details-count');
    this.detailsIncrement?.addEventListener('click', () => {
      if (this.detailsRow) this._onIncrement(this.detailsRow);
    });
    this.detailsDecrement?.addEventListener('click', () => {
      if (this.detailsRow) this._onDecrement(this.detailsRow);
    });
    this.detailsDialog?.addEventListener('click', (event) => {
      if (event.target === this.detailsDialog) this.detailsDialog.close();
    });
  }

  _updateDetailsQuantity() {
    if (!this.detailsRow) return;
    const count = this.counts[this.detailsRow.dataset.variantId];
    if (this.detailsCount) this.detailsCount.textContent = count;
    if (this.detailsDecrement) this.detailsDecrement.disabled = count === 0;
    if (this.detailsIncrement) this.detailsIncrement.disabled = this._totalSelected() >= this.bundleSize || (!this.allowDuplicates && count >= 1);
  }

  _initRail() {
    if (!this.rail) return;
    const previous = this.querySelector('.js-bmm-rail-prev');
    const next = this.querySelector('.js-bmm-rail-next');
    const update = () => {
      previous.disabled = this.rail.scrollLeft <= 2;
      next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
    };
    const move = (direction) => this.rail.scrollBy({
      left: direction * Math.max(this.rail.clientWidth * 0.8, 220),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    this.rail.addEventListener('scroll', update, { passive: true });
    this._railObserver = new ResizeObserver(update);
    this._railObserver.observe(this.rail);
    update();
  }

  disconnectedCallback() {
    this._railObserver?.disconnect();
  }

  _openDetails(row) {
    if (!this.detailsDialog) return;
    this.detailsRow = row;
    const image = this.detailsDialog.querySelector('.js-bmm-details-image');
    const source = row.querySelector('.bundle-mix-match__row-image');
    image.hidden = !source;
    if (source) {
      image.src = source.src;
      image.alt = source.alt;
    }
    const template = row.querySelector('.js-bmm-details-template');
    const title = this.detailsDialog.querySelector('.js-bmm-details-title');
    const formattedTitle = template?.content.querySelector('.bundle-mix-match__details-product-title');
    title.replaceChildren(...Array.from(formattedTitle?.childNodes || [], (child) => child.cloneNode(true)));
    this.detailsDialog.querySelector('.js-bmm-details-family').textContent = row.dataset.variantNotes;
    this.detailsDialog.querySelector('.js-bmm-details-type').textContent = row.querySelector('.bundle-mix-match__row-type')?.textContent || '';
    const body = this.detailsDialog.querySelector('.js-bmm-details-body');
    body.replaceChildren(...Array.from(template?.content.children || [], (child) => child.cloneNode(true)).filter((child) => !child.classList.contains('bundle-mix-match__details-product-title')));
    this._updateDetailsQuantity();
    this.detailsDialog.showModal();
  }

  // ─── Hide floating third-party widgets that overlap the sticky bar ──────────

  _hideFloatingWidgets() {
    const GORGIAS_SELECTOR =
      '#gorgias-chat-container, #gorgias-web-messenger-container, ' +
      '[id^="gorgias"], [class*="gorgias"]';

    const hideMatching = (root) => {
      if (!root) return;
      // Hide the root itself if it matches
      if (root.matches && root.matches(GORGIAS_SELECTOR)) {
        root.style.setProperty('display', 'none', 'important');
      }
      // Hide any matching descendants
      if (root.querySelectorAll) {
        root.querySelectorAll(GORGIAS_SELECTOR).forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
        });
      }
    };

    // Immediate pass — catch anything already in the DOM
    document.querySelectorAll(GORGIAS_SELECTOR).forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

    // MutationObserver — catch late-injected or re-rendered widgets
    this._gorgiasObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            hideMatching(node);
          }
        }
      }
    });

    this._gorgiasObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // ─── Right panel description expand/collapse ────────────────────────────────

  _initDescriptionExpand() {
    const desc = this.querySelector('.js-bmm-desc');
    if (!desc) return;

    desc.classList.add('bundle-mix-match__panel-description--clamped');

    requestAnimationFrame(() => {
      // If nothing is actually clipped, no button needed — remove the clamp
      if (desc.scrollHeight <= desc.offsetHeight + 2) {
        desc.classList.remove('bundle-mix-match__panel-description--clamped');
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-mix-match__desc-toggle';
      btn.textContent = 'Read more';

      btn.addEventListener('click', () => {
        desc.classList.toggle('bundle-mix-match__panel-description--clamped');
        btn.textContent = desc.classList.contains('bundle-mix-match__panel-description--clamped')
          ? 'Read more'
          : 'Read less';
      });

      desc.insertAdjacentElement('afterend', btn);
    });
  }

  // ─── Selection ──────────────────────────────────────────────────────────────

  _totalSelected() {
    return Object.values(this.counts).reduce((sum, n) => sum + n, 0);
  }

  _onIncrement(row) {
    if (this._totalSelected() >= this.bundleSize) return;
    const { variantId } = row.dataset;
    if (!this.allowDuplicates && this.counts[variantId] >= 1) return;
    this.counts[variantId]++;
    this._updateUI();
  }

  _onDecrement(row) {
    const { variantId } = row.dataset;
    if (this.counts[variantId] <= 0) return;
    this.counts[variantId]--;
    this._updateUI();
  }

  // ─── UI Update ──────────────────────────────────────────────────────────────

  _updateUI() {
    const total   = this._totalSelected();
    const atLimit = total >= this.bundleSize;
    const complete = total === this.bundleSize;

    // Panel counter
    if (this.counter) this.counter.textContent = `${total} / ${this.bundleSize}`;

    if (this.trayCounter) this.trayCounter.textContent = `${total} / ${this.bundleSize}`;

    // Hint
    if (this.hint) this.hint.classList.toggle('bundle-mix-match__hint--hidden', complete);

    // Per-row state
    this.rows.forEach((row) => {
      const { variantId } = row.dataset;
      const count = this.counts[variantId];
      row.querySelector('.js-bmm-row-count').textContent = count;
      row.querySelector('.js-bmm-decrement').disabled = count === 0;
      row.querySelector('.js-bmm-increment').disabled = atLimit || (!this.allowDuplicates && count >= 1);
      row.classList.toggle('bundle-mix-match__row--selected', count > 0);
    });

    if (this.trayItems) {
      this.trayItems.replaceChildren();
      this.rows.forEach((row) => {
        const { variantId } = row.dataset;
        const count = this.counts[variantId];
        if (!count) return;
        const item = document.createElement('div');
        item.className = 'bundle-mix-match__tray-item';
        item.dataset.variantId = variantId;
        const source = row.querySelector('.bundle-mix-match__row-image');
        if (source) {
          const image = source.cloneNode();
          image.className = 'bundle-mix-match__tray-image';
          image.alt = '';
          item.append(image);
        }
        const name = document.createElement('span');
        name.textContent = row.dataset.variantTitle;
        const quantity = document.createElement('span');
        quantity.className = 'bundle-mix-match__tray-quantity';
        quantity.textContent = `×${count}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-mix-match__tray-remove';
        remove.textContent = '−';
        remove.setAttribute('aria-label', this.dataset.removeLabel.replace('[title]', row.dataset.variantTitle));
        remove.addEventListener('click', () => {
          this._onDecrement(row);
          const replacement = Array.from(this.trayItems.querySelectorAll('.bundle-mix-match__tray-item'))
            .find((entry) => entry.dataset.variantId === variantId)?.querySelector('.bundle-mix-match__tray-remove');
          if (replacement) replacement.focus();
          else row.querySelector('.js-bmm-increment')?.focus();
        });
        item.append(name, quantity, remove);
        this.trayItems.append(item);
      });
      this.trayEmpty.hidden = total > 0;
    }

    // CTA
    this.cta.disabled = !complete;
    this._updateDetailsQuantity();
  }

  _esc(str) {
    return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ─── Cart ───────────────────────────────────────────────────────────────────

  async _onAddToCart() {
    if (this._adding || this._totalSelected() < this.bundleSize) return;
    this._adding = true;
    this._setLoading(true);
    this._clearError();

    // Build ordered GID array and send as a single line item property
    const gids = [];
    this.rows.forEach((row) => {
      const { variantId } = row.dataset;
      for (let i = 0; i < this.counts[variantId]; i++) {
        gids.push(this.variantData[variantId].gid);
      }
    });
    const properties = { _bundle_selections: JSON.stringify(gids) };

    try {
      const cartItemsComponents = Array.from(document.querySelectorAll('cart-items-component'));
      const sectionIds = cartItemsComponents
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      const body = {
        items: [{ id: parseInt(this.parentVariantId, 10), quantity: 1, properties }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!addRes.ok) {
        let msg = 'Could not add to cart.';
        try { const err = await addRes.json(); msg = err.description || msg; } catch (_) {}
        throw new Error(msg);
      }

      const response = await addRes.json();

      this._adding = false;
      this._setLoading(false);

      this.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'bundle-mix-match',
          data: {
            source: 'product-form-component',
            itemCount: 1,
            productId: this.parentVariantId,
            sections: response.sections,
          },
        },
      }));
    } catch (err) {
      this._adding = false;
      this._setLoading(false);
      this._showError(err.message || 'Could not add to cart.');
    }
  }

  _setLoading(loading) {
    this.cta.disabled = loading;
    this.cta.classList.toggle('bundle-mix-match__cta--loading', loading);
  }

  _clearError() {
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('bundle-mix-match__error--visible');
  }

  _showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.classList.add('bundle-mix-match__error--visible');
  }
}

customElements.define('bundle-mix-match', BundleMixMatch);
